/*
 * Lemonade Training Oy copy rights all right reserved.
 * Notification System Type Definitions
 *
 * Types for the multi-channel notification system including email (SendGrid),
 * push notifications (Expo), user preferences, and audit logging.
 */

import { Timestamp } from "firebase/firestore";

// ====================================================================================================
// Notification Enums (String Literal Unions)
// ====================================================================================================

/**
 * Notification category grouping
 * Used for organizing notification preferences by functional area
 */
export const NOTIFICATION_CATEGORIES = [
  "bookings",
  "payments",
  "classUpdates",
  "account",
  "social",
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/**
 * Categories added after preference docs were already stored: a stored doc
 * may lack them, and a reader falls back to DEFAULT_NOTIFICATION_CATEGORIES.
 */
export const OPTIONAL_NOTIFICATION_CATEGORIES = [
  "social",
] as const satisfies readonly NotificationCategory[];

export type OptionalNotificationCategory =
  (typeof OPTIONAL_NOTIFICATION_CATEGORIES)[number];

/**
 * Delivery channel for notifications
 */
export type NotificationChannel = "email" | "push";

/**
 * All supported notification types in the system
 * Each maps to a specific category and has channel-level configuration
 */
export type NotificationType =
  | "welcome"
  | "password_reset"
  | "payment_receipt"
  | "payment_failed"
  | "membership_activated"
  | "membership_expiring"
  | "booking_confirmed"
  | "booking_cancelled"
  | "class_cancelled"
  | "waitlist_promoted"
  | "class_reminder_24hr"
  | "class_reminder_30min"
  // ── F016 commitment memberships ──
  /** Athlete — pre-renewal reminder inside the legally-required notice window. */
  | "commitment_renewal_notice"
  /** Athlete + owner — payment failed, membership in grace (access unaffected). */
  | "dunning_grace"
  /** Athlete + owner — access paused after grace expired (curable). */
  | "dunning_paused"
  /** Athlete — failed payment recovered, membership restored. */
  | "membership_cured"
  /**
   * Owner — a membership resumed but the paused days could NOT be added back,
   * because the commitment's Stripe schedule was already gone. The athlete was
   * told every paused day would be returned, so this is a promise the platform
   * could not keep: it needs a human to make the member whole, which is why it
   * is typed separately rather than folded into the ordinary cure.
   */
  | "commitment_extension_unappliable"
  /** Owner — new goodwill cancel request awaiting decision. */
  | "membership_request_received"
  /**
   * Athlete — goodwill request outcome (legacy umbrella). Superseded for new
   * writes by membership_request_approved / membership_request_denied; kept
   * because stored rows carry it.
   */
  | "membership_request_resolved"
  /** Athlete — goodwill request approved; access ends per the recorded settlement. */
  | "membership_request_approved"
  /** Athlete — goodwill request denied; the membership continues unchanged. */
  | "membership_request_denied"
  /** Owner — statutory cancellation took effect (awareness only, no health detail). */
  | "membership_statutory_cancelled"
  /** Athlete — their gym disputes the ground and has asked to see proof directly. */
  | "membership_statutory_contested"
  /** Both — a contested statutory cancellation was not substantiated; the membership resumes. */
  | "membership_reinstated"
  /** Athlete — their membership will end at the commitment end date, and can still be undone. */
  | "membership_cancel_scheduled"
  /** Athlete — their membership has ended now. Carries no statutory ground. */
  | "membership_cancel_effective"
  /** Athlete — their early-exit request reached the gym and is awaiting an answer. */
  | "membership_cancel_request_sent"
  // ── Values production already writes; declared late ──
  | "membership_lapsed"
  | "visit_card_expiring"
  | "waitlist_dropped"
  | "purchase_refunded"
  | "member_purchase_refunded"
  | "payment_dispute_created"
  // ── F023 gym access model (category "account") ──
  /** Coach or admin — invited to a gym's staff; re-sent on resend. */
  | "box_staff_invited"
  /** Coach — the owner made them a gym admin. */
  | "box_admin_granted"
  /** Coach — the owner removed their admin role; they remain a coach. */
  | "box_admin_revoked"
  /** Coach — removed from the gym's staff. */
  | "box_staff_removed"
  /** Athlete — their PT programme now belongs to another same-gym coach. */
  | "pt_programme_reassigned"
  /** Athlete — their PT programme stays with the gym. */
  | "pt_programme_kept"
  /** Athlete — their PT programme ended with the coach's departure. */
  | "pt_programme_ended"
  /** Coach — a departing colleague's PT programme was handed to them. */
  | "pt_programme_received"

  // ── F029 calendar sync (category "classUpdates") ──
  /** Athlete, waitlisted athlete or assigned coach — a class they are in moved to a new start time. */
  | "class_rescheduled"

  // ── Social (category "social") ──
  /** Athlete — another athlete fist bumped one of their workout results. */
  | "workout_result_liked";

/**
 * Push data for class_rescheduled. A trigger only: the app re-reads the class
 * by id and never treats these values as evidence. No PII, no class name, no
 * old time.
 */
export type ClassRescheduledPushData = {
  type: "class_rescheduled";
  classID: string;
  boxID: string;
  /** The new start, epoch milliseconds (same encoding as the class_reminder push). */
  startTime: number;
};

/**
 * Every `data.type` a push can carry: one per push-enabled NOTIFICATION_CONFIG
 * row. The app routes each of these; functions and mobile tests fail when the
 * config and this list diverge.
 */
export const PUSH_NOTIFICATION_TYPES = [
  "payment_failed",
  "membership_expiring",
  "membership_lapsed",
  "visit_card_expiring",
  "booking_confirmed",
  "booking_cancelled",
  "class_cancelled",
  "class_rescheduled",
  "waitlist_promoted",
  "waitlist_dropped",
  "class_reminder",
  "purchase_refunded",
  "member_purchase_refunded",
  "payment_dispute_created",
  "membership_statutory_cancelled",
  "membership_request_received",
  "dunning_grace",
  "dunning_paused",
  "membership_cured",
  "commitment_extension_unappliable",
  "commitment_renewal_notice",
  "membership_statutory_contested",
  "workout_result_liked",
] as const satisfies readonly (NotificationType | "class_reminder")[];

export type PushNotificationType = (typeof PUSH_NOTIFICATION_TYPES)[number];

/**
 * Config rows whose push goes out under a different `data.type`. The 30-minute
 * reminder predates the 24hr/30min split and still sends `class_reminder`,
 * which every shipped build routes; renaming it would strand those builds.
 */
export const PUSH_WIRE_TYPE_BY_CONFIG_KEY: Partial<
  Record<NotificationType, PushNotificationType>
> = {
  class_reminder_30min: "class_reminder",
};

/** Longest `PushData.workoutName`, in code points; senders trim and cap to it. */
export const WORKOUT_NAME_MAX_LENGTH = 100;

/**
 * A push's `data`. A trigger only: the app re-reads what it shows by id and
 * validates every id before routing on it. Senders may add fields the app
 * ignores.
 */
export type PushData = {
  type: PushNotificationType;
  classID?: string;
  boxID?: string;
  productUID?: string;
  /** Epoch milliseconds. */
  startTime?: number;
  workoutUID?: string;
  workoutResultUID?: string;
  /** Display title for the opened screen; at most WORKOUT_NAME_MAX_LENGTH. */
  workoutName?: string;
};

/** Android notification channels the app creates on the device. */
export const ANDROID_NOTIFICATION_CHANNEL_IDS = [
  "class-reminders",
  "waitlist",
  "bookings",
] as const;

export type AndroidNotificationChannelId =
  (typeof ANDROID_NOTIFICATION_CHANNEL_IDS)[number];

/**
 * The Android channel a push of each type is sent on; null = no channelId, so
 * Expo's default channel. Expo drops a push whose channel the device has not
 * created, so only ids every shipped build creates may appear here; a new
 * channel needs the sender to know which devices have it first.
 */
export const PUSH_ANDROID_CHANNEL_BY_TYPE: Record<
  NotificationType,
  AndroidNotificationChannelId | null
> = {
  welcome: null,
  password_reset: null,
  payment_receipt: null,
  payment_failed: null,
  membership_activated: null,
  membership_expiring: null,
  booking_confirmed: "bookings",
  booking_cancelled: "bookings",
  class_cancelled: "class-reminders",
  waitlist_promoted: "waitlist",
  class_reminder_24hr: "class-reminders",
  class_reminder_30min: "class-reminders",
  commitment_renewal_notice: null,
  dunning_grace: null,
  dunning_paused: null,
  membership_cured: null,
  commitment_extension_unappliable: null,
  membership_request_received: null,
  membership_request_resolved: null,
  membership_request_approved: null,
  membership_request_denied: null,
  membership_statutory_cancelled: null,
  membership_statutory_contested: null,
  membership_reinstated: null,
  membership_cancel_scheduled: null,
  membership_cancel_effective: null,
  membership_cancel_request_sent: null,
  membership_lapsed: null,
  visit_card_expiring: null,
  waitlist_dropped: "waitlist",
  purchase_refunded: null,
  member_purchase_refunded: null,
  payment_dispute_created: null,
  box_staff_invited: null,
  box_admin_granted: null,
  box_admin_revoked: null,
  box_staff_removed: null,
  pt_programme_reassigned: null,
  pt_programme_kept: null,
  pt_programme_ended: null,
  pt_programme_received: null,
  class_rescheduled: "class-reminders",
  workout_result_liked: null,
};

// ====================================================================================================
// Notification Preferences
// ====================================================================================================

/**
 * Per-channel preference toggle for a notification category
 */
export type ChannelPreference = {
  /** Whether email delivery is enabled for this category */
  email: boolean;
  /** Whether push notification delivery is enabled for this category */
  push: boolean;
};

/** The stored `categories` map: optional categories may be absent. */
export type NotificationCategoryPreferences = Record<
  Exclude<NotificationCategory, OptionalNotificationCategory>,
  ChannelPreference
> &
  Partial<Record<OptionalNotificationCategory, ChannelPreference>>;

/**
 * What a user who never changed a setting gets, per category. Social is push
 * only: a future social email must be an explicit opt-in.
 */
export const DEFAULT_NOTIFICATION_CATEGORIES: Readonly<
  Record<NotificationCategory, Readonly<ChannelPreference>>
> = Object.freeze({
  bookings: Object.freeze({ email: true, push: true }),
  payments: Object.freeze({ email: true, push: true }),
  classUpdates: Object.freeze({ email: true, push: true }),
  account: Object.freeze({ email: true, push: false }),
  social: Object.freeze({ email: false, push: true }),
});

/**
 * A category's effective preference: the stored value per channel when it is
 * a boolean, otherwise that channel's default. Always a fresh object.
 */
export function resolveCategoryPreference(
  categories:
    Partial<Record<NotificationCategory, ChannelPreference>> | null | undefined,
  category: NotificationCategory,
): ChannelPreference {
  const stored: Partial<ChannelPreference> | undefined = categories?.[category];
  const fallback = DEFAULT_NOTIFICATION_CATEGORIES[category];
  return {
    email: typeof stored?.email === "boolean" ? stored.email : fallback.email,
    push: typeof stored?.push === "boolean" ? stored.push : fallback.push,
  };
}

/**
 * Per-user notification preference document
 * Stored in Firestore: Users/{uid}/NotificationPreferences/default
 *
 * Controls which notification channels are active per category,
 * master toggles for each channel, and CAN-SPAM compliance.
 */
export type NotificationPreferences = {
  /** Per-category channel preferences */
  categories: NotificationCategoryPreferences;

  /** Master toggle for push notifications */
  pushEnabled: boolean;
  /** Master toggle for email notifications */
  emailEnabled: boolean;
  /** CAN-SPAM compliant unsubscribe-all flag */
  unsubscribedAll: boolean;

  /** When preferences were last updated */
  updatedAt: Timestamp | Date;
  /** Source of the last preference update */
  updatedFrom: "mobile" | "web" | "email_preferences" | "system";
};

// ====================================================================================================
// Email Notification Payload
// ====================================================================================================

/**
 * Template data payload for sending emails via SendGrid
 * Used by Cloud Functions to construct and dispatch email notifications
 */
export type EmailNotificationPayload = {
  /** Target user ID */
  userId: string;
  /** Recipient email address */
  email: string;
  /** Type of notification being sent */
  notificationType: NotificationType;
  /** SendGrid dynamic template ID */
  templateId: string;
  /** Dynamic template variables for SendGrid */
  dynamicData: Record<string, unknown>;
  /** Optional email subject line override */
  subject?: string;
};

// ====================================================================================================
// Notification Configuration
// ====================================================================================================

/**
 * Channel-level defaults and routing configuration per notification type
 * Used by the notification dispatch system to determine delivery behavior
 */
export type NotificationConfig = {
  /** Which notification type this config applies to */
  type: NotificationType;
  /** Category this notification belongs to */
  category: NotificationCategory;
  /** Per-channel delivery configuration */
  channels: {
    email: {
      /** Whether email is enabled by default for this notification type */
      enabled: boolean;
      /** SendGrid template ID for this notification type */
      templateId: string;
    };
    push: {
      /** Whether push is enabled by default for this notification type */
      enabled: boolean;
    };
  };
  /** If true, notification bypasses user preferences (e.g., security alerts) */
  critical: boolean;
};

// ====================================================================================================
// Email Preference Center Token
// ====================================================================================================

/**
 * JWT payload structure for email preference center authentication
 * Used to generate secure, time-limited links for managing email preferences
 */
export type EmailPreferenceCenterToken = {
  /** User ID */
  uid: string;
  /** User email address */
  email: string;
  /** Token purpose identifier */
  purpose: "email_preferences";
  /** Issued at (Unix timestamp in seconds) */
  iat: number;
  /** Expires at (Unix timestamp in seconds) */
  exp: number;
};

// ====================================================================================================
// Notification Log
// ====================================================================================================

/**
 * Audit trail entry for a dispatched notification
 * Stored in Firestore: NotificationLog/{logId}
 *
 * Records delivery attempt results per channel for debugging and compliance.
 */
export type NotificationLogEntry = {
  /** Firestore document ID */
  id: string;
  /** Target user ID */
  userId: string;
  /** Type of notification that was dispatched */
  notificationType: NotificationType;
  /** Channels the notification was sent through */
  channels: NotificationChannel[];
  /** Per-channel delivery results */
  channelResults: {
    email?: {
      /** Whether email delivery succeeded */
      success: boolean;
      /** SendGrid message ID on success */
      messageId?: string;
      /** Error message on failure */
      error?: string;
    };
    push?: {
      /** Whether push delivery succeeded */
      success: boolean;
      /** Expo push ticket ID on success */
      ticketId?: string;
      /** Error message on failure */
      error?: string;
    };
  };
  /** Additional context metadata for the notification */
  metadata: Record<string, unknown>;
  /** When the notification was dispatched */
  createdAt: Timestamp | Date;
};

// ====================================================================================================
// Push Tickets
// ====================================================================================================

/**
 * An Expo push ticket awaiting its delivery receipt.
 * Stored in Firestore: PushTickets/{receiptId}, the id being Expo's ticket id.
 *
 * Cloud Functions only (rules deny every client). Written when Expo accepts a
 * message, deleted by the receipt sweep once the receipt is read; `expiresAt`
 * drives a Firestore TTL policy as the backstop.
 */
export type PushTicketEntry = {
  /** Recipient user ID */
  uid: string;
  /** The Expo push token the message went to */
  token: string;
  /** The push's `data.type`, or "unknown" when the sender set none */
  type: string;
  /** The Cloud Function export that sent it, when known */
  functionName: string | null;
  createdAt: Timestamp | Date;
  /** The sweep reads the receipt at or after this time */
  checkAfter: Timestamp | Date;
  /** Times the receipt was not yet available */
  attempts: number;
  /** TTL field: Firestore deletes the doc after this time */
  expiresAt: Timestamp | Date;
};

// ====================================================================================================
// Email Queue
// ====================================================================================================

/**
 * Email queue entry for reliable email delivery with retry logic
 * Stored in Firestore: EmailQueue/{emailId}
 *
 * Supports exponential backoff retry and tracks delivery lifecycle.
 */
export type EmailQueueEntry = {
  /** Firestore document ID */
  id: string;
  /** Target user ID */
  userId: string;
  /** Recipient email address */
  email: string;
  /** Type of notification being sent */
  notificationType: NotificationType;
  /** SendGrid dynamic template ID */
  templateId: string;
  /** Dynamic template variables for SendGrid */
  dynamicData: Record<string, unknown>;

  /**
   * Current delivery status.
   * `expired` = deliberately not sent because the entry outlived its type's max
   * retry age (its payload went stale — e.g. a password-reset link whose code
   * has already expired). Distinct from `failed`, which means delivery was
   * attempted and did not succeed.
   */
  status: "pending" | "processing" | "sent" | "failed" | "bounced" | "expired";
  /** Number of delivery attempts made */
  attempts: number;
  /** Maximum delivery attempts before permanent failure */
  maxAttempts: number;

  /** When the last delivery attempt was made */
  lastAttemptAt?: Timestamp | Date;
  /** When the next retry should be attempted */
  nextRetryAt?: Timestamp | Date;
  /** When the queue entry was created */
  createdAt: Timestamp | Date;
  /** When the email was successfully sent */
  sentAt?: Timestamp | Date;
  /** When the entry was expired without being sent (set with status `expired`) */
  expiredAt?: Timestamp | Date;
  /** Why the entry was expired — kept separate from `error` so the original delivery failure stays diagnosable */
  expiryReason?: string;
  /** Error message from the most recent failed attempt */
  error?: string;
};
